/**
 * Zone attacks (backlog B-18/B-19).
 *
 * A `zone` cast anchors a timed area at its target's position: `windupLeft` ticks of
 * telegraph, then a strike every `strikeEveryTicks` for `ticksLeft` ticks, full damage
 * to everything inside with no falloff (the same R-05-17 reasoning as AoE). A tracking
 * zone (B-19) drifts toward its mark at a capped speed and re-marks the nearest enemy
 * when the mark dies — a slow searchlight the enemy tries to outrun.
 *
 * Zones are a fourth entity type for the same reason drones are: they neither travel
 * like projectiles nor persist like towers. Like a projectile, a zone outlives the
 * tower that cast it — its damage was committed at cast time.
 */
import { DT } from '../constants.js';
import type { Enemy, EntityPool, Tower, Zone } from '../entities.js';
import type { SimEvent } from '../events.js';
import { angleTo, cos, distSq, sin } from '../math.js';
import { NO_TARGET, type EntityId } from '../types.js';
import { applyDamage, type DamageInput } from './damage.js';
import { applyOnHitHooks, type HookContext } from './hooks.js';
import type { SpatialGrid } from './spatial.js';

/**
 * Hard cap, drone-style (R-06-19's reasoning): skipped at the cap, never queued. Ten
 * zone towers at the slowest authored cadence sit far below this — the cap is a
 * runaway backstop, not an expected limit.
 */
export const MAX_LIVE_ZONES = 40;

/** Tiles a tracking zone searches for a fresh mark once its current one dies. */
export const ZONE_RETARGET_RANGE = 4;

export interface ZoneContext {
  tick: number;
  zones: EntityPool<Zone>;
  enemies: EntityPool<Enemy>;
  towers: EntityPool<Tower>;
  grid: SpatialGrid;
  events: SimEvent[];
  scratch: EntityId[];
  hooks: HookContext;
}

export function canSpawnZone(zones: EntityPool<Zone>): boolean {
  return zones.countAlive() < MAX_LIVE_ZONES;
}

/**
 * Advance every live zone one tick: drift, wind up, strike, expire.
 *
 * Deaths are only tombstoned here (damage goes through the shared pipeline), so this
 * loop never sees the enemy array change under it — resolution happens in system 10
 * like everything else (R-05-20).
 */
export function stepZones(ctx: ZoneContext): void {
  const items = ctx.zones.items;

  for (let i = 0; i < items.length; i++) {
    const z = items[i] as Zone;
    if (!z.alive) continue;

    if (z.trackSpeed > 0) drift(z, ctx);

    if (z.windupLeft > 0) {
      z.windupLeft--;
      // The first strike lands the moment the telegraph completes — a windup that
      // ended with another strike-interval of silence would read as a dud.
      if (z.windupLeft === 0) strike(z, ctx);
      continue;
    }

    if (--z.ticksLeft <= 0) {
      ctx.zones.kill(z);
      continue;
    }

    z.sinceStrike++;
    if (z.sinceStrike >= z.strikeEveryTicks) {
      z.sinceStrike = 0;
      strike(z, ctx);
    }
  }
}

/** Move toward the mark, re-marking the nearest enemy in reach when it is gone. */
function drift(z: Zone, ctx: ZoneContext): void {
  let target = ctx.enemies.get(z.targetId);
  if (!target) {
    target = nearestEnemy(z, ctx);
    z.targetId = target ? target.id : NO_TARGET;
  }
  if (!target) return;

  const step = z.trackSpeed * DT;
  const d = Math.sqrt(distSq(z.x, z.y, target.x, target.y));
  if (d <= step) {
    z.x = target.x;
    z.y = target.y;
    return;
  }
  const angle = angleTo(z.x, z.y, target.x, target.y);
  z.x += cos(angle) * step;
  z.y += sin(angle) * step;
}

function nearestEnemy(z: Zone, ctx: ZoneContext): Enemy | undefined {
  ctx.grid.query(z.x, z.y, ZONE_RETARGET_RANGE, ctx.enemies, ctx.scratch);

  let best: Enemy | undefined;
  let bestDist = 0;
  for (let i = 0; i < ctx.scratch.length; i++) {
    const candidate = ctx.enemies.get(ctx.scratch[i] as EntityId);
    if (!candidate) continue;
    const dist = distSq(z.x, z.y, candidate.x, candidate.y);
    // Ties on lowest id, as everywhere else that picks among entities (R-05-05).
    if (!best || dist < bestDist || (dist === bestDist && candidate.id < best.id)) {
      best = candidate;
      bestDist = dist;
    }
  }
  return best;
}

/** Full damage to everything inside, through the shared pipeline (R-05-17 semantics). */
function strike(z: Zone, ctx: ZoneContext): void {
  ctx.events.push({ t: 'zoneStruck', zone: z.id, x: z.x, y: z.y, radius: z.radius, kind: z.kind });

  // The caster may have been sold since the cast — the strike still lands (it was
  // committed, like a projectile in flight), it just runs no on-hit hooks.
  const owner = ctx.towers.get(z.ownerTower);
  const input: DamageInput = { amount: z.damage, kind: z.kind, crit: z.crit, tower: owner };

  ctx.grid.query(z.x, z.y, z.radius, ctx.enemies, ctx.scratch);
  // Copied because on-hit hooks may run nested grid queries that reuse `scratch`.
  const ids = ctx.scratch.slice();
  for (let i = 0; i < ids.length; i++) {
    const victim = ctx.enemies.get(ids[i] as EntityId);
    if (!victim) continue;
    applyDamage(victim, input, ctx.tick, ctx.events);
    if (owner) applyOnHitHooks(ctx.hooks, owner, victim);
  }
}
